import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, X, Loader2, BookOpen, FileText, List, MessageSquare, FlaskConical } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import {
  getAgent,
  updateAgent,
  getAgentConversations,
  getScenario,
  AIAgent,
  ConversationMessage,
  LEAD_AGENT_GOAL_OPTIONS,
  type LeadAgentGoalId,
} from '../lib/aiAgents';
import AgentChatPanel from '../components/ai-agents/AgentChatPanel';
import AgentRulesPanel from '../components/ai-agents/AgentRulesPanel';
import AgentScenariosPanel from '../components/ai-agents/AgentScenariosPanel';
import AgentListsPanel from '../components/ai-agents/AgentListsPanel';
import LeadAgentSimulateModal from '../components/ai-agents/LeadAgentSimulateModal';
import toast from 'react-hot-toast';

export default function AIAgentBuilder() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();

  const [agent, setAgent] = useState<AIAgent | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [rulesRefreshTrigger, setRulesRefreshTrigger] = useState(0);
  const [scenariosRefreshTrigger, setScenariosRefreshTrigger] = useState(0);

  // Scenario mode state
  const [chatMode, setChatMode] = useState<'training' | 'scenario'>('training');
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);
  const [scenarioMessages, setScenarioMessages] = useState<ConversationMessage[]>([]);

  // Right panel tab
  const [rightTab, setRightTab] = useState<'rules' | 'scenarios' | 'lists'>('rules');
  const [listsRefreshTrigger, setListsRefreshTrigger] = useState(0);
  const [simulateOpen, setSimulateOpen] = useState(false);

  useEffect(() => {
    if (agentId) loadAgent();
  }, [agentId]);

  const loadAgent = async () => {
    if (!agentId) return;
    try {
      const data = await getAgent(agentId);
      if (!data) {
        toast.error('Agent not found');
        navigate('/ai-agents');
        return;
      }
      setAgent(data);
      setNameValue(data.name);

      const convos = await getAgentConversations(agentId);
      if (convos.length > 0) {
        setConversationId(convos[0].id);
        setMessages(convos[0].messages || []);
      }
    } catch (err) {
      console.error('Failed to load agent:', err);
      toast.error('Failed to load agent');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!agent || !nameValue.trim()) return;
    setSavingName(true);
    try {
      const updated = await updateAgent(agent.id, { name: nameValue.trim() });
      setAgent(updated);
      setEditingName(false);
    } catch (err) {
      toast.error('Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleConversationUpdate = useCallback((newConvId: string, newMessages: ConversationMessage[]) => {
    if (newConvId) setConversationId(newConvId);
    setMessages(newMessages);
  }, []);

  const handleRulesChanged = useCallback(() => {
    setRulesRefreshTrigger(prev => prev + 1);
  }, []);

  const handleToggleStatus = async () => {
    if (!agent) return;
    const newStatus = agent.status === 'active' ? 'draft' : 'active';
    try {
      const updated = await updateAgent(agent.id, { status: newStatus });
      setAgent(updated);
      toast.success(newStatus === 'active' ? 'Agent activated' : 'Agent set to draft');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleStartScenario = useCallback(() => {
    setChatMode('scenario');
    setActiveScenarioId(null);
    setScenarioMessages([]);
    setRightTab('scenarios');
  }, []);

  const handleExitScenario = useCallback(() => {
    setChatMode('training');
    setActiveScenarioId(null);
    setScenarioMessages([]);
  }, []);

  const handleScenarioUpdate = useCallback((newScenarioId: string, newMessages: ConversationMessage[]) => {
    if (newScenarioId) setActiveScenarioId(newScenarioId);
    setScenarioMessages(newMessages);
  }, []);

  const handleScenarioSaved = useCallback(() => {
    setChatMode('training');
    setActiveScenarioId(null);
    setScenarioMessages([]);
    setScenariosRefreshTrigger(prev => prev + 1);
  }, []);

  const handleEditScenario = useCallback(async (scenarioId: string) => {
    try {
      const scenario = await getScenario(scenarioId);
      if (scenario) {
        setChatMode('scenario');
        setActiveScenarioId(scenario.id);
        setScenarioMessages(scenario.messages || []);
        setRightTab('scenarios');
      } else {
        toast.error('Scenario not found');
      }
    } catch {
      toast.error('Failed to load scenario');
    }
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!agent) return null;

  const statusColor = agent.status === 'active'
    ? 'bg-green-100 text-green-700 hover:bg-green-200'
    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200';

  return (
    <div
      className="h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100"
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200/60 px-4 py-3 flex items-center gap-3 shadow-sm flex-shrink-0">
        <button
          onClick={() => navigate('/ai-agents')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>

        <span className="text-xl">{agent.icon || '🤖'}</span>

        {editingName ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              className="text-lg font-bold text-gray-900 border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500 outline-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') { setEditingName(false); setNameValue(agent.name); }
              }}
            />
            <button onClick={handleSaveName} disabled={savingName} className="p-1 hover:bg-gray-100 rounded">
              <Check className="h-4 w-4 text-green-600" />
            </button>
            <button onClick={() => { setEditingName(false); setNameValue(agent.name); }} className="p-1 hover:bg-gray-100 rounded">
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        ) : (
          <h1
            className="text-lg font-bold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => setEditingName(true)}
            title="Click to rename"
          >
            {agent.name}
          </h1>
        )}

        <button
          onClick={handleToggleStatus}
          className={`ml-2 text-xs font-medium px-2.5 py-1 rounded-full capitalize transition-colors ${statusColor}`}
        >
          {agent.status}
        </button>

        {agent.purpose && (
          <span className="text-sm text-gray-400 hidden md:inline ml-2 truncate max-w-xs">
            {agent.purpose}
          </span>
        )}
      </div>

      {agent.agent_kind === 'lead' && (
        <div className="bg-violet-50 border-b border-violet-100 px-4 py-2.5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs text-violet-900 font-medium flex-shrink-0">
              <MessageSquare className="h-4 w-4 flex-shrink-0" />
              <span>Lead agent</span>
            </div>
            <div className="flex items-center gap-2 min-w-0 max-w-full sm:max-w-md">
              <span className="text-[11px] font-semibold text-violet-800 whitespace-nowrap">Goal</span>
              <select
                value={(agent.lead_goal as LeadAgentGoalId) || 'gather_info_sales_call'}
                onChange={async (e) => {
                  const v = e.target.value as LeadAgentGoalId;
                  try {
                    const updated = await updateAgent(agent.id, { lead_goal: v });
                    setAgent(updated);
                    toast.success('Lead goal updated');
                  } catch {
                    toast.error('Failed to update goal');
                  }
                }}
                className="flex-1 min-w-0 text-xs border border-violet-200 rounded-lg px-2 py-1.5 bg-white text-violet-900 font-medium"
              >
                {LEAD_AGENT_GOAL_OPTIONS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:flex-shrink-0">
            <button
              type="button"
              onClick={() => setSimulateOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-violet-800 bg-white border border-violet-200 hover:bg-violet-50 transition-colors"
            >
              <FlaskConical className="w-3.5 h-3.5" />
              Simulate
            </button>
            <label className="flex items-center gap-2 text-sm font-medium text-violet-900 cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded border-violet-300 text-violet-600 focus:ring-violet-500"
                checked={!!agent.tool_config?.sms?.enabled}
                onChange={async (e) => {
                  const enabled = e.target.checked;
                  try {
                    const nextConfig = { ...agent.tool_config, sms: { enabled } };
                    const updated = await updateAgent(agent.id, { tool_config: nextConfig });
                    setAgent(updated);
                    toast.success(enabled ? 'SMS enabled for this lead agent' : 'SMS disabled');
                  } catch {
                    toast.error('Failed to update tool settings');
                  }
                }}
              />
              SMS
            </label>
          </div>
        </div>
      )}

      {agent.agent_kind === 'lead' && selectedBusiness?.id && (
        <LeadAgentSimulateModal
          open={simulateOpen}
          onClose={() => setSimulateOpen(false)}
          agentId={agent.id}
          businessId={selectedBusiness.id}
          agentName={agent.name}
        />
      )}

      {/* Split Panels */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Chat */}
        <div className="flex-1 flex flex-col min-w-0 border-r border-gray-200/60 bg-white">
          <AgentChatPanel
            agentId={agent.id}
            agentName={agent.name}
            agentPurpose={agent.purpose}
            businessId={selectedBusiness?.id || ''}
            conversationId={conversationId}
            messages={messages}
            onConversationUpdate={handleConversationUpdate}
            onRulesChanged={handleRulesChanged}
            mode={chatMode}
            scenarioId={activeScenarioId}
            scenarioMessages={scenarioMessages}
            onStartScenario={handleStartScenario}
            onExitScenario={handleExitScenario}
            onScenarioUpdate={handleScenarioUpdate}
            onScenarioSaved={handleScenarioSaved}
          />
        </div>

        {/* Right: Rules / Scenarios */}
        <div className="w-80 lg:w-96 flex-shrink-0 bg-gray-50 flex flex-col min-h-0 hidden md:flex">
          {/* Tab Switcher */}
          <div className="flex border-b border-gray-200 bg-white flex-shrink-0">
            <button
              onClick={() => setRightTab('rules')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                rightTab === 'rules'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <BookOpen className="h-3.5 w-3.5" />
              Rules
            </button>
            <button
              onClick={() => setRightTab('scenarios')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                rightTab === 'scenarios'
                  ? 'text-amber-600 border-b-2 border-amber-600 bg-amber-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FileText className="h-3.5 w-3.5" />
              Scenarios
            </button>
            <button
              onClick={() => setRightTab('lists')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                rightTab === 'lists'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <List className="h-3.5 w-3.5" />
              Lists
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 min-h-0">
            {rightTab === 'rules' ? (
              <AgentRulesPanel
                agentId={agent.id}
                refreshTrigger={rulesRefreshTrigger}
              />
            ) : rightTab === 'scenarios' ? (
              <AgentScenariosPanel
                agentId={agent.id}
                refreshTrigger={scenariosRefreshTrigger}
                onEditScenario={handleEditScenario}
              />
            ) : (
              <AgentListsPanel
                agentId={agent.id}
                refreshTrigger={listsRefreshTrigger}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
